import { DEFAULT_LOCALE, LOCALES } from './config';

/**
 * Locale detection and path helpers — pure functions, no Next imports, so the
 * proxy can call them and the tests can cover them without a server.
 *
 * Detection order (see Documentation/13 §2.3):
 *   1. the switcher's cookie, if it names an enabled locale;
 *   2. Accept-Language, by q-value, matched on the PRIMARY subtag
 *      (es-MX → es, pt-BR → pt, en-GB → en);
 *   3. the default locale.
 */

interface AcceptEntry {
  tag: string;
  q: number;
  index: number;
}

/**
 * Parse an Accept-Language header into language tags ordered by preference:
 * highest q first, header order as the tie-break. Entries with q=0 and
 * malformed entries are dropped. Tags come back lower-cased.
 */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  const entries: AcceptEntry[] = [];
  header.split(',').forEach((part, index) => {
    const [rawTag = '', ...params] = part.trim().split(';');
    const tag = rawTag.trim().toLowerCase();
    if (!tag || !/^[a-z0-9*-]+$/.test(tag)) return;
    let q = 1;
    for (const param of params) {
      const match = /^\s*q\s*=\s*(.*?)\s*$/i.exec(param);
      if (!match) continue;
      // A q we cannot read is not a preference we can rank: drop the entry.
      const value = match[1] === '' ? NaN : Number(match[1]);
      q = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
    }
    if (q > 0) entries.push({ tag, q, index });
  });
  entries.sort((a, b) => b.q - a.q || a.index - b.index);
  return entries.map((e) => e.tag);
}

/** "pt-BR" → "pt"; "es-419" → "es"; "*" → "*". */
export function primarySubtag(tag: string): string {
  return tag.toLowerCase().split('-')[0] ?? '';
}

/**
 * The first preferred tag whose primary subtag is an enabled locale, else the
 * fallback. A wildcard never matches — the fallback covers it.
 */
export function matchLocale(
  preferred: readonly string[],
  enabled: readonly string[] = LOCALES,
  fallback: string = DEFAULT_LOCALE,
): string {
  for (const tag of preferred) {
    const primary = primarySubtag(tag);
    if (enabled.includes(primary)) return primary;
  }
  return fallback;
}

export interface PickLocaleInput {
  cookie?: string | null;
  acceptLanguage?: string | null;
  enabled?: readonly string[];
  fallback?: string;
}

/** Cookie beats header beats default. Unknown cookie values are ignored. */
export function pickLocale(input: PickLocaleInput): string {
  const enabled = input.enabled ?? LOCALES;
  const fallback = input.fallback ?? DEFAULT_LOCALE;
  if (input.cookie && enabled.includes(input.cookie)) return input.cookie;
  return matchLocale(parseAcceptLanguage(input.acceptLanguage), enabled, fallback);
}

/**
 * "/es/standings" → { locale: "es", path: "/standings" }
 * "/standings"    → { locale: null, path: "/standings" }
 * "/es"           → { locale: "es", path: "/" }
 * Matching is exact-case: "/ES/…" is not a locale path (Next's segment would
 * not validate either), so it redirects like any un-prefixed path.
 */
export function splitLocaleFromPath(
  pathname: string,
  enabled: readonly string[] = LOCALES,
): { locale: string | null; path: string } {
  const match = /^\/([^/]+)(\/.*)?$/.exec(pathname);
  if (match && enabled.includes(match[1]!)) {
    return { locale: match[1]!, path: match[2] || '/' };
  }
  return { locale: null, path: pathname || '/' };
}

/**
 * "/standings" + "es" → "/es/standings"; "/" + "es" → "/es".
 * The default locale is unprefixed: "/standings" + "en" → "/standings".
 * Mirrors next-intl's `localePrefix: 'as-needed'` (routing.ts) so links,
 * canonicals, the sitemap and the proxy all agree on one URL per page.
 */
export function localizePath(path: string, locale: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === '/' ? `/${locale}` : `/${locale}${clean}`;
}
